import type { KeyPair } from "@foreman/crypto";
import { CloseCode, fromBase64, toBase64, type AgentUsage, type ApprovalRule, type DeviceInfo, type PowerSettings, type PowerState, type DiffFile, type GitInfo, type GitOp, type FolderEntry, type HistoryEntry, type InboxEvent, type Message, type SessionInfo, type SkillInfo, type SkillOp, type CatalogSkill, type TranscriptItem } from "@foreman/protocol";
import { InitiatorLink } from "./links";

export interface GitDiffReply {
  sessionId: string;
  state: "ok" | "not_repo" | "git_missing" | "no_folder" | "error";
  message?: string;
  branch?: string;
  files: DiffFile[];
  truncated: boolean;
  /** Where the branch stands (computers that know how). */
  info?: GitInfo;
  /** Set when this is a comparison with another branch. */
  against?: string;
}

/** The answers to `gitBranches`, `gitLog`, `gitShow` and `gitDo`, each carrying the `rid` it was asked with. */
export type GitReply = Extract<Message, { t: "git.branched" | "git.logged" | "git.shown" | "git.done" }>;

/** Everything the computer says about the team: the state, history pages, recaps, live activity, terminals, and done/failed answers. */
export type OfficeReply = Extract<
  Message,
  { t: "office.state" | "office.history.listed" | "office.summary.shown" | "office.activity" | "office.message" | "office.messages.listed" | "office.terminal.shown" | "office.done" }
>;

export interface Transport {
  send(data: Uint8Array): void;
  close(code?: number, reason?: string): void;
}
export interface TransportHandlers {
  onOpen(): void;
  /** Binary frames only. */
  onMessage(data: Uint8Array): void;
  onClose(code: number, reason: string): void;
}
/** Opens one connection to the relay. Node uses `ws`; React Native will use `WebSocket`. */
export type Connector = (handlers: TransportHandlers) => Transport;

/** `paused`: the computer has been offline for a long while, so retrying stopped; `reconnectNow()` resumes. */
export type ClientState = "connecting" | "ready" | "disconnected" | "failed" | "paused";

export interface RemoteClientEvents {
  onState?(state: ClientState, detail?: string): void;
  onSessions?(sessions: SessionInfo[]): void;
  onSessionCreated?(session: SessionInfo): void;
  /** The whole inbox (newest first): sent after connecting and whenever asked. */
  onInbox?(events: InboxEvent[]): void;
  /** One inbox event, new or changed. */
  onInboxEvent?(event: InboxEvent): void;
  onInboxRemoved?(eventId: string): void;
  /** How much of each agent's plan limits is used: the answer to `getUsage`. */
  onUsage?(usage: { agents: AgentUsage[]; at: number; complete?: boolean }): void;
  /** The computer's approval rules: the answer to `getRules`, and sent again whenever they change. */
  onRules?(rules: ApprovalRule[]): void;
  /** Anything about the team on the computer (see `OfficeReply`). Replies to a request carry its `rid`. */
  onOffice?(reply: OfficeReply): void;
  /** Reply to `findFiles`. */
  /** Reply to `skillsDo`. */
  onSkills?(reply: { rid: string; ok: boolean; message?: string; skills: SkillInfo[]; catalog?: CatalogSkill[]; library: string }): void;
  onFilesFound?(found: { rid: string; results: string[]; truncated: boolean }): void;
  /** Reply to `findFolders`. */
  onFolderFound?(found: { rid: string; root: string; results: FolderEntry[]; truncated: boolean; error?: string; denied?: boolean }): void;
  /** Reply to `listFolders`. */
  onFolders?(listing: { path: string; parent: string | null; dirs: FolderEntry[]; truncated: boolean; recent?: FolderEntry[]; places?: FolderEntry[] }): void;
  /** Reply to `listHistory`. */
  onHistory?(cwd: string, items: HistoryEntry[]): void;
  /** Reply to `gitDiff`. */
  onGitDiff?(result: GitDiffReply): void;
  /** Reply to `gitBranches` / `gitLog` / `gitShow` / `gitDo`. */
  onGitReply?(reply: GitReply): void;
  /** Reply to `listTranscript`. */
  onTranscript?(sessionId: string, items: TranscriptItem[], before: number | null, info?: { end?: number; local?: "idle" | "busy"; reset?: boolean }): void;
  /** The computer linked itself to the signed-in account. */
  onAccountAttached?(account: { email: string; name: string | null }): void;
  onAccountState?(state: { signedIn: boolean; hint: string | null; plan?: "guest" | "free" | "pro" }): void;
  /** The computer's own account of how it is kept reachable (also pushed when the power source or the timeout changes it). */
  onPowerState?(state: PowerState): void;
  /** Whether this computer can turn a recording into text (reply to `getVoice`). */
  onVoiceState?(state: { ready: boolean; reason?: string; limitSeconds?: number; needs?: "model" | "program"; settingUp?: boolean }): void;
  /** Where "Set up voice" is on the computer. */
  onVoiceSetup?(progress: { stage: "installing" | "downloading" | "checking" | "done" | "error"; percent?: number; message?: string }): void;
  /** Reply to `transcribe`. */
  onVoiceTranscribed?(r: { rid: string; ok: boolean; text?: string; message?: string }): void;
  /** Reply to `uploadImage`. */
  onImageSaved?(r: { rid: string; ok: boolean; path?: string; message?: string }): void;
  /** Reply to `sendWake`: the packet left this computer (or why not). It does not say the other computer woke up. */
  onWakeSent?(r: { mac: string; ok: boolean; message?: string }): void;
  onDevices?(devices: DeviceInfo[]): void;
  /** The computer confirmed it removed this device. If it was this device, the connection ends and the state becomes "failed". */
  onDeviceRevoked?(deviceId: string): void;
  /** Per session, contiguous and duplicate-free, even across reconnects. */
  onOutput?(sessionId: string, offset: number, data: Uint8Array): void;
  /** Output between `expected` and `actual` was evicted from the daemon's scrollback before we could fetch it. */
  onGap?(sessionId: string, expected: number, actual: number): void;
  onExit?(sessionId: string, exitCode: number | null): void;
  onError?(error: { sessionId?: string; eventId?: string; code: string; message: string; feature?: string }): void;
  /** A session's mode changed or a switch was refused (`session.mode`), and everything about a chat (`chat.*`). */
  onChat?(msg: ChatMessage): void;
}

/** Messages about a session's chat (and its mode), handed to `onChat` as they arrive. */
export type ChatMessage = Extract<Message, { t: "session.mode" | "chat.state" | "chat.delta" | "chat.message" | "chat.tool" | "chat.approval" | "chat.status" | "chat.result" | "chat.allowAll" }>;

export interface RemoteClientOptions {
  /**
   * Let the computer send the current screen instead of a long replay when this client is far behind (default true: right
   * for anything that draws a terminal). Turn off for a client that needs every byte, such as a recorder.
   */
  snapshots?: boolean;
  keys: KeyPair;
  daemonPublicKey: Uint8Array;
  connect: Connector;
  events: RemoteClientEvents;
  /** Reconnect delay grows from min to max (ms), and resets once a connection is ready. */
  backoff?: { minMs: number; maxMs: number };
  /** Give up on a connection that doesn't finish the handshake in this long (ms). */
  handshakeTimeoutMs?: number;
  /**
   * When the computer itself is not connected to the relay (it is off, asleep or Brocoded is not running), knocking every
   * few seconds helps nobody. Retries then start at `minMs`, double up to `maxMs`, and stop after `giveUpMs` with a
   * "paused" state; `reconnectNow()` (a tap, the app coming back to the front) starts again.
   */
  offlineRetry?: { minMs: number; maxMs: number; giveUpMs: number };
}

/**
 * Phone-side client: keeps one encrypted connection to a daemon alive through the relay.
 * It remembers, per attached session, the offset it has received up to, and after every
 * reconnect re-attaches from there, so the app sees an unbroken output stream.
 */
export class RemoteClient {
  private transport: Transport | null = null;
  private link: InitiatorLink | null = null;
  private stopped = true;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private handshakeTimer: ReturnType<typeof setTimeout> | null = null;
  private delay: number;
  private offlineSince: number | null = null;
  private offlineDelay = 0;
  /** sessionId -> next offset we expect (everything before it was delivered). */
  private readonly attached = new Map<string, number>();
  /** Sessions whose chat is open here: followed again after every reconnect. */
  private readonly chats = new Set<string>();
  private readonly backoff: { minMs: number; maxMs: number };
  private readonly offlineRetry: { minMs: number; maxMs: number; giveUpMs: number };
  private readonly handshakeTimeoutMs: number;

  constructor(private readonly opts: RemoteClientOptions) {
    this.backoff = opts.backoff ?? { minMs: 250, maxMs: 10_000 };
    this.offlineRetry = opts.offlineRetry ?? { minMs: 5_000, maxMs: 60_000, giveUpMs: 10 * 60_000 };
    this.handshakeTimeoutMs = opts.handshakeTimeoutMs ?? 10_000;
    this.delay = this.backoff.minMs;
  }

  get ready(): boolean {
    return this.link?.ready === true;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    const t = this.transport;
    this.transport = null;
    this.link = null;
    t?.close(1000, "client stopped");
    this.opts.events.onState?.("disconnected", "stopped");
  }

  /**
   * Drop the current connection, if any, and reconnect right now (skipping any backoff wait).
   * Call it when the app returns to the foreground or the network comes back: after a suspend
   * a socket can look open while the path is dead, and reconnecting is cheap because output is
   * replayed from the last offset. Does nothing after stop() or once the device was rejected.
   */
  reconnectNow(): void {
    if (this.stopped) return;
    this.clearTimers();
    const t = this.transport;
    this.link = null; // ignore anything the old socket still delivers
    this.transport = null;
    t?.close(1000, "reconnecting");
    this.delay = this.backoff.minMs;
    this.offlineSince = null; // a deliberate retry starts the offline clock again
    this.offlineDelay = 0;
    this.open();
  }

  // Requests. They return false when not connected: nothing is queued, so a keystroke
  // typed while offline can never be replayed later against a different screen.
  listSessions(): boolean {
    return this.send({ t: "session.list" });
  }

  /** Hand this phone's Brocoded sign-in to the computer so it links itself to the account. */
  attachAccount(token: string): boolean {
    return this.send({ t: "account.attach", token });
  }

  /** This phone is signing out: ask the computer to sign out of the same account. The new account state comes back. */
  detachAccount(token: string): boolean {
    return this.send({ t: "account.detach", token });
  }

  /** Ask whether the computer is signed in (a masked hint comes back). */
  getAccount(): boolean {
    return this.send({ t: "account.get" });
  }

  /** Ask how much of each agent's plan limits is used. Answered with `onUsage` (an older computer answers with an `unsupported` error). */
  getUsage(refresh = false): boolean {
    return this.send({ t: "usage.get", ...(refresh ? { refresh: true } : {}) });
  }

  /** Ask for the approval rules kept on the computer. Answered with `onRules` (an older computer answers with an `unsupported` error). */
  getRules(): boolean {
    return this.send({ t: "rules.get" });
  }

  addRule(rule: Omit<ApprovalRule, "id" | "createdAt">): boolean {
    return this.send({ t: "rules.add", rule });
  }

  removeRule(id: string): boolean {
    return this.send({ t: "rules.remove", id });
  }

  /** Ask for the team on this computer. Answered with `onOffice` (`office.state`; an older computer answers with an `unsupported` error). */
  getOffice(): boolean {
    return this.send({ t: "office.get" });
  }

  /** What agents have done, newest first. Pass the previous reply's `nextBefore` as `before` for the next page. Answered with `office.history.listed`. */
  officeHistory(rid: string, q: { agentId?: string; before?: string; limit?: number; kinds?: Extract<Message, { t: "office.history" }>["kinds"] } = {}): boolean {
    return this.send({ t: "office.history", rid, ...q });
  }

  /** The latest messages the team passed on (hand-overs, answers to the person). Answered with `office.messages.listed`. */
  officeMessages(rid: string, limit?: number): boolean {
    return this.send({ t: "office.messages", rid, ...(limit ? { limit } : {}) });
  }

  /** A recap of one agent's work. Answered with `office.summary.shown`. */
  officeSummary(rid: string, agentId: string): boolean {
    return this.send({ t: "office.summary", rid, agentId });
  }

  /** Give the team a goal; its orchestrator splits it up. Answered with `office.done`. */
  officeSend(rid: string, body: string, subject?: string): boolean {
    return this.send({ t: "office.send", rid, body, ...(subject ? { subject } : {}) });
  }

  /** Pause, resume, stop or steer one agent. Answered with `office.done`, then a fresh `office.state`. */
  officeControl(rid: string, agentId: string, action: "pause" | "resume" | "halt" | "steer", text?: string): boolean {
    return this.send({ t: "office.control", rid, agentId, action, ...(text ? { text } : {}) });
  }

  /** The agent's screen as text. Answered with `office.terminal.shown`. */
  officeTerminal(rid: string, agentId: string): boolean {
    return this.send({ t: "office.terminal", rid, agentId });
  }

  /** Type into an agent's terminal. Answered with `office.done`. */
  officeInput(rid: string, agentId: string, text: string, enter = false): boolean {
    return this.send({ t: "office.input", rid, agentId, text, ...(enter ? { enter: true } : {}) });
  }

  /** Start or stop the team on the computer. Answered with `office.done`, then a fresh `office.state`. */
  officeEngine(rid: string, action: "start" | "stop"): boolean {
    return this.send({ t: "office.engine", rid, action });
  }

  /** Show or hide the team engine's own window on the computer. Answered with `office.done`. */
  officeWindow(rid: string, visible: boolean): boolean {
    return this.send({ t: "office.window", rid, visible });
  }

  /** Ask how the computer is kept reachable. Answered with `onPowerState`. */
  getPower(): boolean {
    return this.send({ t: "power.get" });
  }

  /** Change how the computer is kept reachable. `onPowerState` comes back once the computer has applied it. */
  setPower(settings: PowerSettings): boolean {
    return this.send({ t: "power.set", settings });
  }

  /** Ask whether voice input is ready on this computer. Answered with `onVoiceState` (an older computer answers with an `unsupported` error). */
  getVoice(): boolean {
    return this.send({ t: "voice.get" });
  }

  /** Send a short recording (base64) to be turned into text on the computer. Answered with `onVoiceTranscribed`. */
  transcribe(rid: string, mime: string, audio: string, piece?: { seq: number; last: boolean }): boolean {
    return this.send({ t: "voice.transcribe", rid, mime, audio, ...(piece ? { seq: piece.seq, last: piece.last } : {}) });
  }

  /** Send a photo (base64) to be saved on the computer. Answered with `onImageSaved`. */
  uploadImage(rid: string, mime: string, data: string, piece?: { seq: number; last: boolean }): boolean {
    return this.send({ t: "image.upload", rid, mime, data, ...(piece ? { seq: piece.seq, last: piece.last } : {}) });
  }

  /** Ask the computer to get voice ready by itself (it downloads the speech model). Progress comes back as `onVoiceSetup`, then `onVoiceState`. */
  setupVoice(): boolean {
    return this.send({ t: "voice.setup" });
  }

  /** Ask this computer to send a wake-on-LAN packet to another computer on its network. Answered with `onWakeSent`. */
  sendWake(mac: string, broadcast: string): boolean {
    return this.send({ t: "power.wake", mac, broadcast });
  }

  endSession(sessionId: string): boolean {
    return this.send({ t: "session.end", sessionId });
  }

  /** Tell the computer where to wake this phone (its browser push address), or `null` to stop being woken. */
  setPushEndpoint(endpoint: string | null, extra?: { keys?: { p256dh: string; auth: string }; details?: boolean; finished?: boolean; quiet?: { from: number; to: number; tz: string } }): boolean {
    return this.send(endpoint ? { t: "push.subscribe", endpoint, ...(extra?.keys && { keys: extra.keys }), ...(extra?.details && { details: true }), ...(extra?.finished === false && { finished: false }), ...(extra?.quiet && { quiet: extra.quiet }) } : { t: "push.unsubscribe" });
  }

  removeSession(sessionId: string): boolean {
    return this.send({ t: "session.remove", sessionId });
  }

  /** Subfolders of `path`, or the home folder and recent projects when omitted. */
  listFolders(path?: string): boolean {
    return this.send({ t: "fs.list", ...(path === undefined ? {} : { path }) });
  }

  /** Ask the skill library on the computer to do something (or just list). Answered with `onSkills`. */
  skillsDo(rid: string, op: SkillOp, cwd?: string): boolean {
    return this.send({ t: "skills.do", rid, op, ...(cwd ? { cwd } : {}) });
  }

  /** Files and folders in the session's folder that match `query`. Answered with `onFilesFound`. */
  findFiles(rid: string, sessionId: string, query: string): boolean {
    return this.send({ t: "files.find", sessionId, rid, query });
  }

  /** Folders whose name matches `query` anywhere under `root` (default: the home folder). Answered with `onFolderFound`. */
  findFolders(rid: string, query: string, root?: string): boolean {
    return this.send({ t: "fs.find", rid, query, ...(root === undefined ? {} : { root }) });
  }

  /** Earlier messages of a session's conversation: the latest page, or the one before cursor `before`. */
  listTranscript(sessionId: string, before?: number, limit = 30): boolean {
    return this.send({ t: "session.transcript", sessionId, ...(before === undefined ? {} : { before }), limit });
  }

  /** Only what a conversation gained after `after` (an `end` from an earlier reply), to follow it live. */
  followTranscript(sessionId: string, after: number): boolean {
    return this.send({ t: "session.transcript", sessionId, after, limit: 30 });
  }

  /** What has changed (git) in the folder `sessionId` runs in. */
  gitDiff(sessionId: string, against?: string): boolean {
    return this.send({ t: "git.diff", sessionId, ...(against ? { against } : {}) });
  }

  gitBranches(sessionId: string, rid: string): boolean {
    return this.send({ t: "git.branches", sessionId, rid });
  }

  gitLog(sessionId: string, rid: string, skip = 0, limit = 30): boolean {
    return this.send({ t: "git.log", sessionId, rid, skip, limit });
  }

  gitShow(sessionId: string, rid: string, hash: string): boolean {
    return this.send({ t: "git.show", sessionId, rid, hash });
  }

  /** Change the repository. Risky operations are refused with code "busy" while the agent works, unless `ignoreBusy`. */
  gitDo(sessionId: string, rid: string, op: GitOp, ignoreBusy?: boolean): boolean {
    return this.send({ t: "git.do", sessionId, rid, op, ...(ignoreBusy ? { ignoreBusy: true } : {}) });
  }

  /** Earlier agent conversations in `cwd` (answered with the same `cwd`). */
  listHistory(cwd: string): boolean {
    return this.send({ t: "agent.history", cwd });
  }

  listInbox(): boolean {
    return this.send({ t: "inbox.list" });
  }

  /** Approve/deny an approval, or answer a question (`answers`: one text per question, in order). */
  respondInbox(eventId: string, action: "approve" | "deny" | "answer", answers?: string[]): boolean {
    return this.send({ t: "inbox.respond", eventId, action, ...(answers === undefined ? {} : { answers }) });
  }

  dismissInbox(eventId: string): boolean {
    return this.send({ t: "inbox.dismiss", eventId });
  }

  listDevices(): boolean {
    return this.send({ t: "devices.list" });
  }

  revokeDevice(deviceId: string): boolean {
    return this.send({ t: "devices.revoke", deviceId });
  }

  createSession(opts: { command: string; args?: string[]; cwd?: string; cols?: number; rows?: number; title?: string; takeOver?: string; force?: boolean }): boolean {
    return this.send({ t: "session.create", command: opts.command, args: opts.args ?? [], cwd: opts.cwd, cols: opts.cols ?? 80, rows: opts.rows ?? 24, ...(opts.title ? { title: opts.title } : {}), ...(opts.takeOver ? { takeOver: opts.takeOver } : {}), ...(opts.takeOver && opts.force ? { force: true } : {}) });
  }

  /** Start (or keep) receiving a session. Survives reconnects. Replays from `fromOffset`. */
  attach(sessionId: string, fromOffset = 0): void {
    this.attached.set(sessionId, fromOffset);
    if (this.ready) this.send(this.attachMessage(sessionId, fromOffset));
  }

  private attachMessage(sessionId: string, fromOffset: number) {
    return { t: "session.attach" as const, sessionId, fromOffset, ...(this.opts.snapshots === false ? {} : { snapshot: true }) };
  }

  detach(sessionId: string): void {
    this.attached.delete(sessionId);
  }

  input(sessionId: string, data: string | Uint8Array): boolean {
    const bytes = typeof data === "string" ? new TextEncoder().encode(data) : data;
    return this.send({ t: "input", sessionId, data: toBase64(bytes) });
  }

  resize(sessionId: string, cols: number, rows: number): boolean {
    return this.send({ t: "resize", sessionId, cols, rows });
  }

  /** Run the session's agent as a chat, or back in its terminal. Answered with `session.mode`. */
  setMode(sessionId: string, mode: "terminal" | "chat"): boolean {
    return this.send({ t: "session.mode.set", sessionId, mode });
  }

  /** Follow a session's chat (kept across reconnects): `chat.state`, then live `chat.*`. */
  chatAttach(sessionId: string): void {
    this.chats.add(sessionId);
    if (this.ready) this.send({ t: "chat.attach", sessionId });
  }

  chatDetach(sessionId: string): void {
    if (this.chats.delete(sessionId)) this.send({ t: "chat.detach", sessionId });
  }

  chatSend(sessionId: string, text: string): boolean {
    return this.send({ t: "chat.send", sessionId, text });
  }

  chatInterrupt(sessionId: string): boolean {
    return this.send({ t: "chat.interrupt", sessionId });
  }

  /** "Allow everything in this chat" on or off. */
  chatAllowAll(sessionId: string, on: boolean): boolean {
    return this.send({ t: "chat.allowAll.set", sessionId, on });
  }

  chatApprove(sessionId: string, reqId: string, allow: boolean): boolean {
    return this.send({ t: "chat.approve", sessionId, reqId, allow });
  }

  private send(msg: Message): boolean {
    if (!this.link?.ready) return false;
    this.link.send(msg);
    return true;
  }

  private open(): void {
    this.opts.events.onState?.("connecting");
    const link = new InitiatorLink({
      keys: this.opts.keys,
      daemonPublicKey: this.opts.daemonPublicKey,
      sendRaw: (f) => this.transport?.send(f),
      onReady: () => this.onReady(),
      onMessage: (m) => this.onMessage(m),
    });
    this.link = link;
    const transport = this.opts.connect({
      onOpen: () => {
        if (this.link !== link) return;
        this.handshakeTimer = setTimeout(() => this.drop("handshake timed out"), this.handshakeTimeoutMs);
        link.start();
      },
      onMessage: (data) => {
        if (this.link !== link) return;
        try {
          link.receive(data);
        } catch (e) {
          this.opts.events.onError?.({ code: "link", message: (e as Error).message });
          this.drop((e as Error).message);
        }
      },
      onClose: (code, reason) => {
        if (this.link !== link) return; // a connection we already abandoned
        this.onClosed(code, reason);
      },
    });
    this.transport = transport;
  }

  private onReady(): void {
    this.clearHandshakeTimer();
    this.delay = this.backoff.minMs;
    this.offlineSince = null;
    this.offlineDelay = 0;
    this.opts.events.onState?.("ready");
    for (const [sessionId, fromOffset] of this.attached) {
      this.send(this.attachMessage(sessionId, fromOffset));
    }
    for (const sessionId of this.chats) this.send({ t: "chat.attach", sessionId });
  }

  /** When the computer last sent anything at all (any message). The app uses it to notice a link that looks up but is silent. */
  heardAt = 0;

  private onMessage(msg: Message): void {
    this.heardAt = Date.now();
    const ev = this.opts.events;
    switch (msg.t) {
      case "session.listed":
        ev.onSessions?.(msg.sessions);
        break;
      case "session.created":
        ev.onSessionCreated?.(msg.session);
        break;
      case "fs.listed":
        ev.onFolders?.({ path: msg.path, parent: msg.parent, dirs: msg.dirs, truncated: msg.truncated, recent: msg.recent, places: msg.places });
        break;
      case "skills.state":
        ev.onSkills?.({ rid: msg.rid, ok: msg.ok, message: msg.message, skills: msg.skills, catalog: msg.catalog, library: msg.library });
        break;
      case "files.found":
        ev.onFilesFound?.({ rid: msg.rid, results: msg.results, truncated: msg.truncated });
        break;
      case "fs.found":
        ev.onFolderFound?.({ rid: msg.rid, root: msg.root, results: msg.results, truncated: msg.truncated, error: msg.error, denied: msg.denied });
        break;
      case "git.diffed": {
        const { t: _t, ...reply } = msg;
        ev.onGitDiff?.(reply);
        break;
      }
      case "git.branched":
      case "git.logged":
      case "git.shown":
      case "git.done":
        ev.onGitReply?.(msg);
        break;
      case "agent.history.listed":
        ev.onHistory?.(msg.cwd, msg.items);
        break;
      case "inbox.listed":
        ev.onInbox?.(msg.events);
        break;
      case "inbox.event":
        ev.onInboxEvent?.(msg.event);
        break;
      case "inbox.removed":
        ev.onInboxRemoved?.(msg.eventId);
        break;
      case "usage.state":
        ev.onUsage?.({ agents: msg.agents, at: msg.at, complete: msg.complete });
        break;
      case "rules.listed":
        ev.onRules?.(msg.rules);
        break;
      case "office.state":
      case "office.history.listed":
      case "office.summary.shown":
      case "office.activity":
      case "office.message":
      case "office.messages.listed":
      case "office.terminal.shown":
      case "office.done":
        ev.onOffice?.(msg);
        break;
      case "account.state":
        ev.onAccountState?.({ signedIn: msg.signedIn, hint: msg.hint, ...(msg.plan ? { plan: msg.plan } : {}) });
        break;
      case "power.state":
        ev.onPowerState?.(msg.state);
        break;
      case "voice.state":
        ev.onVoiceState?.({ ready: msg.ready, ...(msg.reason ? { reason: msg.reason } : {}), ...(msg.limitSeconds ? { limitSeconds: msg.limitSeconds } : {}), ...(msg.needs ? { needs: msg.needs } : {}), ...(msg.settingUp ? { settingUp: true } : {}) });
        break;
      case "voice.setup.progress":
        ev.onVoiceSetup?.({ stage: msg.stage, ...(msg.percent !== undefined ? { percent: msg.percent } : {}), ...(msg.message ? { message: msg.message } : {}) });
        break;
      case "voice.transcribed":
        ev.onVoiceTranscribed?.({ rid: msg.rid, ok: msg.ok, ...(msg.text !== undefined ? { text: msg.text } : {}), ...(msg.message ? { message: msg.message } : {}) });
        break;
      case "image.saved":
        ev.onImageSaved?.({ rid: msg.rid, ok: msg.ok, ...(msg.path !== undefined ? { path: msg.path } : {}), ...(msg.message ? { message: msg.message } : {}) });
        break;
      case "power.wake.sent":
        ev.onWakeSent?.({ mac: msg.mac, ok: msg.ok, ...(msg.message ? { message: msg.message } : {}) });
        break;
      case "account.attached":
        ev.onAccountAttached?.({ email: msg.email, name: msg.name });
        break;
      case "devices.listed":
        ev.onDevices?.(msg.devices);
        break;
      case "devices.revoked":
        ev.onDeviceRevoked?.(msg.deviceId);
        break;
      case "output":
        this.onOutput(msg.sessionId, msg.offset, fromBase64(msg.data));
        break;
      case "session.transcripted":
        ev.onTranscript?.(msg.sessionId, msg.items, msg.before, { end: msg.end, local: msg.local, reset: msg.reset });
        break;
      case "snapshot": {
        // The computer sent the current screen instead of a long replay. It starts with a terminal reset, so it replaces
        // whatever was shown; our position becomes the offset it ends at.
        if (!this.attached.has(msg.sessionId)) break;
        this.attached.set(msg.sessionId, msg.offset);
        ev.onOutput?.(msg.sessionId, msg.offset, fromBase64(msg.data));
        break;
      }
      case "session.exited":
        ev.onExit?.(msg.sessionId, msg.exitCode);
        break;
      case "error":
        ev.onError?.({ sessionId: msg.sessionId, eventId: msg.eventId, code: msg.code, message: msg.message, ...(msg.feature ? { feature: msg.feature } : {}) });
        break;
      case "session.mode":
      case "chat.state":
      case "chat.delta":
      case "chat.message":
      case "chat.tool":
      case "chat.approval":
      case "chat.status":
      case "chat.allowAll":
      case "chat.result":
        ev.onChat?.(msg);
        break;
      default:
        break; // client-to-daemon messages are not expected from the daemon
    }
  }

  private onOutput(sessionId: string, offset: number, data: Uint8Array): void {
    const expected = this.attached.get(sessionId);
    if (expected === undefined) return; // detached while output was in flight
    let start = offset;
    let bytes = data;
    if (offset > expected) {
      this.opts.events.onGap?.(sessionId, expected, offset);
    } else if (offset < expected) {
      const overlap = expected - offset;
      if (overlap >= data.length) return; // entirely something we already delivered
      bytes = data.subarray(overlap);
      start = expected;
    }
    this.attached.set(sessionId, start + bytes.length);
    this.opts.events.onOutput?.(sessionId, start, bytes);
  }

  /** Abandon the current connection and schedule a retry. */
  private drop(reason: string): void {
    const t = this.transport;
    this.link = null; // ignore anything the dying socket still delivers
    this.transport = null;
    t?.close(4400, reason.slice(0, 100));
    this.onClosed(4400, reason);
  }

  private onClosed(code: number, reason: string): void {
    this.clearTimers();
    this.link = null;
    this.transport = null;
    if (this.stopped) return;
    if (code === CloseCode.DeviceRejected) {
      this.stopped = true;
      this.opts.events.onState?.("failed", "this device is not authorized on that computer");
      return;
    }
    if (code === CloseCode.GuestComputerLimit) {
      this.stopped = true; // retrying cannot help: a sign-in (which restarts the connection) can
      this.opts.events.onState?.("failed", "guest_computer_limit");
      return;
    }
    this.opts.events.onState?.("disconnected", `${code} ${reason}`.trim());
    let wait = this.delay;
    this.delay = Math.min(this.delay * 2, this.backoff.maxMs);
    if (code === CloseCode.DaemonOffline || code === CloseCode.DaemonGone) {
      // the computer is not there: wait longer each time, and after a while stop knocking until asked
      const now = Date.now();
      this.offlineSince ??= now;
      if (now - this.offlineSince >= this.offlineRetry.giveUpMs) {
        this.opts.events.onState?.("paused", `${code} ${reason}`.trim());
        return;
      }
      this.offlineDelay = this.offlineDelay === 0 ? this.offlineRetry.minMs : Math.min(this.offlineDelay * 2, this.offlineRetry.maxMs);
      wait = this.offlineDelay;
    } else {
      this.offlineSince = null;
      this.offlineDelay = 0;
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) this.open();
    }, wait);
  }

  private clearHandshakeTimer(): void {
    if (this.handshakeTimer) clearTimeout(this.handshakeTimer);
    this.handshakeTimer = null;
  }

  private clearTimers(): void {
    this.clearHandshakeTimer();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }
}
